/* ══════════════════════════════════════════════════════════════════════════
 * THE ONE PLACE THAT TALKS TO THE SERVER
 *
 * ⚠️ EVERY CALL GOES THROUGH HERE. Not because it is tidy — because the whole
 * of today was one fault: two copies of one thing that drifted apart. Three
 * search bars, two cuisine lists, two sidebars, a column named in one file and
 * created in another. The moment a second screen writes its own `fetch` with
 * its own idea of where the server is and how a refusal looks, that fault is
 * back and it will not be found until somebody is standing in front of it.
 *
 * ⚠️ THE ADDRESS IS NEVER TYPED INTO A SCREEN. It comes from VITE_API_URL, so
 * moving the server is a Render setting rather than a hunt through the code.
 * ══════════════════════════════════════════════════════════════════════════ */

const BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
/* the server's address, for the one call that sends raw sound (voice.js) */
export const API_BASE = BASE;

/* ⚠️ THE LEGAL PAGES ARE SERVED BY `eleat-api`, NOT BY THE APP.
 * `/privacy`, `/terms`, `/cookies`, `/guidelines` and `/contact` live in
 * `4. Backend/src/modules/site/` — the app is a static site and cannot serve
 * them. So a link to one is the API host plus the path, which is exactly this
 * constant and must not be typed out a second time anywhere: the address has
 * already changed once this week, and two copies of it would have meant every
 * legal link in Eleat pointing at yesterday's server.
 * ⚠️ AND IT RETURNS NOTHING WHEN THE ADDRESS IS UNSET, so a caller can draw
 * plain text rather than a link to nowhere. */
export function sitePage(path) {
  return BASE ? BASE + path : null;
}

/* ⚠️ SAY IT LOUDLY AND EARLY. A missing address makes every call fail with a
 * browser message that looks like the server is down, and the real cause —
 * a setting nobody filled in — is invisible. */
if (!BASE) {
  console.error(
    '[eleat] VITE_API_URL is not set. Nothing can reach the server. ' +
    'Set it in Render to the address of eleat-api.'
  );
}

/* ── WHERE THE TOKEN LIVES ────────────────────────────────────────────────
 * ⚠️ "Stay signed in" decides WHICH box, and the screen says exactly what it
 * does: "Eleat will not ask again on this device."
 *   ticked  → localStorage   survives closing the browser
 *   unticked→ sessionStorage gone when the tab closes
 *
 * ⚠️ AND IT IS TRUE OF THIS DEVICE ONLY. Migration 082 recorded that there is
 * no session table and no way to end a token early from elsewhere, so the
 * screen must not imply otherwise — and it does not.
 */
const KEY = 'eleat.token';

export function saveToken(token, stay) {
  clearToken();
  (stay ? localStorage : sessionStorage).setItem(KEY, token);
}

/* the same sign-in, renewed — the server hands a new token after a password
   change; it replaces the old one where it was kept, and nothing else moves
   (saveToken also forgets the account's language) */
export function replaceToken(token) {
  try {
    if (localStorage.getItem(KEY)) localStorage.setItem(KEY, token);
    else sessionStorage.setItem(KEY, token);
  } catch { /* storage refused */ }
}

/* ⚠️ SIGNED IN UNTIL THEY SIGN OUT — Al-Noor, 12 Oct 2026: "once they are
   signed in, you keep them signed in until they log out." Once a day, while
   the app is open, the sign-in is swapped for a fresh one (POST /auth/renew),
   so it never runs out. A failure changes nothing; only the server saying the
   sign-in itself is over (a password changed elsewhere) signs anyone out. */
export function keepSignedIn() {
  try {
    const t = getToken();
    if (!t) return;
    const last = Number(localStorage.getItem('eleat.renewedAt') || 0);
    if (Date.now() - last < 20 * 3600 * 1000) return;
    fetch(`${BASE}/auth/renew`, { method: 'POST', headers: { Authorization: `Bearer ${t}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.token && getToken() === t) { replaceToken(d.token); localStorage.setItem('eleat.renewedAt', String(Date.now())); }
      })
      .catch(() => { /* tried again next time */ });
  } catch { /* storage refused */ }
}

/* ⚠️ SIGNING IN NO LONGER RELOADS THE APP — Al-Noor, 9 Oct 2026: "once I
   signed in that took a while for it to log me in and put me onto the home
   page". A reload fetched the page and the code again across the world before
   Home could ask for anything. What remembered "signed out" (the hearts, the
   Feed likes, a visit waiting to be claimed) listens for this instead. */
export const SIGNED_IN_EVENT = 'eleat:signed-in';
export function announceSignedIn() {
  try { window.dispatchEvent(new Event(SIGNED_IN_EVENT)); } catch { /* old browser */ }
}

export function getToken() {
  try {
    return localStorage.getItem(KEY) || sessionStorage.getItem(KEY) || null;
  } catch {
    return null;                      // private browsing can refuse storage
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
    /* signed out is English again (Al, 6 Oct 2026) — the account's language
       leaves with the account; signing in brings it back (Home) */
    localStorage.removeItem('eleat.lang');
    localStorage.removeItem('eleat.lang.chosen');
  } catch { /* nothing to do */ }
}

/* ── THE CALL ─────────────────────────────────────────────────────────────── */

/* ⚠️ A REQUEST THAT NEVER ANSWERS MUST EVENTUALLY SAY SO — 23 September 2026.
 *
 * Al sent a photograph of his own homepage: all four rails reading "Loading…",
 * for as long as he watched, with no error and nothing to tap. Nothing was
 * broken — the same four requests answer correctly, and the screen draws them
 * correctly. `fetch` simply has NO TIMEOUT of its own. If the server is asleep
 * (Render stops a service that has had no traffic, and the first request after
 * that waits while it starts), or a phone is on a network that drops the
 * connection without closing it, the promise never settles — so `.then` never
 * runs, `.catch` never runs, and the screen sits on its loading line forever.
 *
 * All four rails at once is the signature of ONE server not answering, not of
 * four broken queries. An Eleater cannot know that, and "Loading…" tells them
 * nothing and offers them nothing.
 *
 * ⚠️ AND IT IS 45 SECONDS, NOT FIVE. A sleeping service genuinely takes most
 * of a minute to wake, and a short timeout would turn a slow-but-working first
 * visit into a failure. This is the line between "slow" and "never".
 *
 * ⚠️⚠️⚠️ AND "MOST OF A MINUTE" WAS A GUESS THAT TURNED OUT TO BE WRONG —
 * MEASURED 29 SEPTEMBER 2026.
 *
 * Al's own phone: all four rails red with *"Could not reach Eleat. Check your
 * connection and try again."* — after he had signed in successfully seconds
 * earlier, so his connection was plainly fine and Eleat told him it was not.
 *
 * **The same request was then timed against the live server from a second
 * machine, twice in a row.** The first call — the first traffic since that
 * morning's deploy — did not answer inside **180 seconds**. The second, a
 * moment later, answered in about one. Nothing was broken: the service was
 * asleep, and waking it took four times the window this file allows.
 *
 * ⚠️ SO THE 45 SECONDS ABOVE IS NOT WRONG, IT IS INCOMPLETE. It is the right
 * line between "slow" and "never" for a server that is awake. It is the wrong
 * line for one that is starting, and every rail on Home asks at once, so a
 * cold open spends all four of its requests on the wake and shows four red
 * lines for a server that was about to answer.
 *
 * ⚠️ ONE MORE ATTEMPT, AND ONLY FOR A READ. The first attempt is what wakes
 * the service, so the second is the one with a chance — which is why the
 * second window is longer rather than the first being stretched to three
 * minutes, where an Eleater would simply put the phone down.
 *
 * ⚠️⚠️ AND NEVER FOR A WRITE. Repeating a POST is how somebody joins twice,
 * books a table twice or leaves two reviews — a timed-out write may well have
 * SUCCEEDED and lost only its answer. Reads are safe to repeat by definition;
 * writes are not, and no amount of screen polish is worth a double booking.
 *
 * ⚠️ AND ONLY WHEN THE SERVER SAID NOTHING AT ALL. A 4xx, a 5xx or a refusal
 * is an answer and is never retried — that is the rule `provider.js` already
 * follows for Zaryna, and it is the same rule here rather than a second one.
 *
 * ⚠️ THIS IS A PATCH OVER A THING THAT IS NOT ELEAT'S TO FIX. The service
 * sleeps because of the plan it is on; the app cannot make a sleeping server
 * fast, it can only stop calling it dead before it wakes. Keeping it awake
 * costs money and is Al's call. */
const GIVE_UP_AFTER_MS = 45000;
const GIVE_UP_AFTER_WAKING_MS = 120000;
const BREATH_MS = 400;

export async function call(path, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  try {
    return await attempt(path, opts, GIVE_UP_AFTER_MS);
  } catch (e) {
    /* `offline` is set only by the network branch below — never by a real
       answer from the server, however bad that answer was. */
    if (method !== 'GET' || !e.offline) throw e;
    await new Promise((r) => setTimeout(r, BREATH_MS));
    return attempt(path, opts, GIVE_UP_AFTER_WAKING_MS);
  }
}

/* THE PRIVATE PREVIEW (17 Oct 2026): the token the password screen was given rides on every call;
   a 423 from the server means the password is needed (or has changed) — the app opens ?preview=1 */
export const PREVIEW_EVENT = 'eleat:preview';
export function previewToken() { try { return localStorage.getItem('eleat.preview') || ''; } catch { return ''; } }
export function savePreviewToken(t) { try { if (t) localStorage.setItem('eleat.preview', t); else localStorage.removeItem('eleat.preview'); } catch { /* storage blocked */ } }
function previewNeeded() {
  try { window.dispatchEvent(new CustomEvent(PREVIEW_EVENT)); } catch { /* an old browser */ }
}

async function attempt(path, { method = 'GET', body, auth = false } = {}, giveUpMs) {
  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  const pv = previewToken();
  if (pv) headers['X-Eleat-Preview'] = pv;
  if (auth) {
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  const stopwatch = new AbortController();
  const timer = setTimeout(() => stopwatch.abort(), giveUpMs);

  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      signal: stopwatch.signal,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    /* ⚠️ A REFUSED CONNECTION IS NOT A WRONG PASSWORD, and must never be shown
     * as one. This is the branch that fires when the server is asleep, the
     * address is wrong, or the browser blocked the call — and an Eleater who
     * is told their password is wrong will change a password that was fine. */
    const e = new Error('Could not reach Eleat. Check your connection and try again.');
    e.cause = networkError;
    e.offline = true;
    /* ⚠️ THE SAME SENTENCE FOR BOTH, ON PURPOSE. A refused connection and a
     * request that ran out of time are the same thing to the person holding
     * the phone: Eleat did not answer. `timedOut` is here for a screen that
     * wants to offer a retry, not for a second message. */
    e.timedOut = networkError && networkError.name === 'AbortError';
    throw e;
  } finally {
    clearTimeout(timer);
  }

  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* not JSON */ }

  /* ⚠️ A SIGN-IN THE SERVER HAS ENDED IS FORGOTTEN — audit, 7 Oct 2026. A
     password changed elsewhere (401) or an account deleted or suspended (403
     "This account is suspended" / "not available") left the token on the
     phone, every screen refused, and Sign out was not on screen because the
     account could not load — no way out but clearing the browser. Only these
     answers; a 403 about a restaurant or a screen is not about the sign-in. */
  if (auth && headers.Authorization && (res.status === 401
      || (res.status === 403 && /suspended|not available/i.test(String((data && data.error) || ''))))) {
    clearToken();
  }

  if (res.status === 423 && data && data.preview) previewNeeded();

  if (!res.ok) {
    /* ⚠️ THE SERVER'S OWN WORDS, WHEN IT GAVE ANY. It already writes the
     * careful ones — "That number or email and password do not match" is
     * deliberately the same for a wrong password and an account that does not
     * exist, so a stranger cannot learn who is on Eleat. Replacing it here
     * would undo that. */
    /* ⚠️ "Validation failed" IS NOT FOR A PERSON — audit, 7 Oct 2026: a
       mistyped email showed it. The field the server names is answered in
       Eleat's own words (Reset's "Enter your email address."). */
    const bad = data && Array.isArray(data.details) ? data.details.map((x) => x && x.field) : [];
    const msg = (res.status === 422 && bad.includes('email')) ? 'Enter your email address.'
              : (data && (data.error || data.message)) || `Something went wrong (${res.status}).`;
    const e = new Error(msg);
    e.status = res.status;
    e.data = data;
    throw e;
  }

  return data;
}

/* ── SIGN IN ──────────────────────────────────────────────────────────────
 * ⚠️ ONE FIELD, EITHER KIND. Al, 16 September: "Type in your phone number and
 * your password. That's it." The server takes `phone` OR `email` and looks in
 * both columns (auth.service.js signInWithPassword), so the screen asks once
 * and this decides which name to send it under.
 *
 * ⚠️ THE TEST IS DELIBERATELY BLUNT: does it contain an "@". Anything cleverer
 * starts guessing about addresses, and the only cost of being wrong is that
 * the server says no to a value it was never going to match anyway.
 */
/* ── JOINING ──────────────────────────────────────────────────────────────
 * Two calls, in this order, and the second one is the only one that returns
 * anything you can sign in with.
 *
 *   POST /auth/eleater/start    → 202 { sent_to, expires_in_minutes }
 *   POST /auth/eleater/verify   → 201 { token, eleater_id, customer_no }
 *
 * ⚠️ `POST /auth/eleater/register` IS GONE AND MUST NOT COME BACK. It returned
 * a signed token for any account matched by email or phone, with no proof of
 * anything — including a super_admin's. Defect B1, 27 August 2026. The route
 * still exists purely to answer 410 loudly, so a client still calling it fails
 * somewhere a person reads rather than 404ing into silence.
 *
 * ⚠️⚠️ THE NUMBER IS NEVER SENT TO EITHER OF THESE FROM THE EMAIL DOOR, and
 * this is the trap in the whole flow. `verifyEleater` reads
 * `destination = input.phone || input.email` — so a body carrying both
 * verifies the PHONE against a code that was emailed, and its insert sets
 * `phone_verified = !!input.phone`. Passing the number along "because the
 * screen collected it" would mark a number verified that nobody ever proved
 * they hold, which is defect B1 wearing a different hat.
 *
 * The number is saved afterwards, through PUT /settings/me, which sets
 * `phone_verified = false` on its way past — correct, because at that point it
 * is a number somebody typed and nothing more. 04_Email_SignUp calls it what
 * it is: "Eleat sends your reminders here."
 */
/* ⚠️ A NUMBER OR AN ADDRESS, NEVER BOTH (8 Oct 2026, the WhatsApp door). The
   server sends the code to whichever is given — sending both would verify one
   against a code sent to the other (see joinVerify's note above). */
const doorOf = ({ email, phone }) => (phone
  ? { phone: String(phone).trim() }
  : { email: String(email || '').trim().toLowerCase() });

export function joinStart({ email, phone, market }) {
  return call('/auth/eleater/start', {
    method: 'POST',
    body: { ...doorOf({ email, phone }), ...(market ? { market } : {}) },
  });
}

export function joinVerify({ email, phone, code, new_password, first_name, last_name, date_of_birth, market }) {
  return call('/auth/eleater/verify', {
    method: 'POST',
    body: {
      ...doorOf({ email, phone }),
      code: code.trim(),
      /* ⚠️ THE PASSWORD GOES WITH THE CODE — audit, 7 Oct 2026: Join passed
         it and this line dropped it, so the server's fix (verifyEleater saves
         new_password) never received one; the fallback /password/set then
         ended the session it had just started. */
      ...(new_password ? { new_password } : {}),
      /* ⚠️ THE ROUTE CALLS THE FIRST NAME `name`, NOT `first_name`, and the
         schema is not strict here so a wrong key would be dropped in silence
         and the account would be created nameless. Checked against
         auth.routes.js eleaterVerify, not remembered. */
      ...(first_name ? { name: first_name } : {}),
      ...(last_name ? { last_name } : {}),
      /* ⚠️ yyyy-mm-dd, ASSEMBLED BY THE SCREEN. The route's own comment: "a
         date split across three fields is a widget detail and this is an API."
         ⚠️ AND IT ARRIVES HERE OR IT ARRIVES NOWHERE — migration 057. Drop it
         and `signup_completed_at` stays null, which the schema reads as an
         unfinished account.

         ⚠️ THIS COMMENT USED TO END "which is the shape
         eleater_may_see_alcohol() reads as unfinished… would be shown no bar
         menu, ever". THERE IS NO AGE GATE IN ELEAT and there never will be —
         migration 097 dropped that function and its column on Al's
         instruction, restating migration 070. The date of birth is
         demographics and a completeness check, never a gate. */
      ...(date_of_birth ? { date_of_birth } : {}),
      ...(market ? { market } : {}),
    },
  });
}

/** The first password. Needs the token `joinVerify` just handed back. */
export function setPassword({ new_password, confirm_password, current_password }) {
  return call('/auth/password/set', {
    method: 'POST', auth: true,
    body: {
      new_password, confirm_password,
      ...(current_password ? { current_password } : {}),
    },
  });
}

/* ── FORGOTTEN IT ─────────────────────────────────────────────────────────
 * ⚠️ `/reset/start` ANSWERS THE SAME WHETHER OR NOT THE ACCOUNT EXISTS, on
 * purpose — it is how a stranger is stopped from typing addresses at Eleat to
 * learn who is on it. The screen must not improve on that by saying "we have
 * sent you a code" only sometimes.
 *
 * ⚠️ AND A CORRECT CODE BUYS A NEW PASSWORD, NOT A SESSION. `/reset/finish`
 * returns no token; migration 087 bumps `token_version`, which ends every
 * session that was already open. So the screen sends them to sign in with the
 * password they just chose, and anyone holding a stolen token is out.
 */
export function resetStart({ identifier }) {
  const id = identifier.trim();
  return call('/auth/password/reset/start', {
    method: 'POST',
    /* side — 144: the same email may also hold a restaurant login; this is the Eleater's */
    body: { side: 'eleater', ...(id.includes('@') ? { email: id.toLowerCase() } : { phone: id }) },
  });
}

export function resetFinish({ identifier, code, new_password, confirm_password }) {
  const id = identifier.trim();
  return call('/auth/password/reset/finish', {
    method: 'POST',
    body: {
      side: 'eleater',
      ...(id.includes('@') ? { email: id.toLowerCase() } : { phone: id }),
      code: code.trim(), new_password, confirm_password,
    },
  });
}

/** Save anything on the account — names, the number, the preferences. */
export function saveMe(body) {
  return call('/settings/me', { method: 'PUT', auth: true, body });
}

/* ── WHO AM I ─────────────────────────────────────────────────────────────
 * ⚠️ THE TOKEN DOES NOT CARRY A NAME, AND SHOULD NOT. It holds the account id,
 * the kind of account and the token version (auth.js signToken) — nothing a
 * screen can read as a person. So the name is asked for, once, after signing
 * in, rather than smuggled into the token where it would go stale the moment
 * somebody changed it in Settings and never refresh until they signed out. */
export function me() {
  return call('/settings/me', { auth: true });
}

/* ── A VENUE ──────────────────────────────────────────────────────────────
 * ⚠️ EIGHT TABS, AND EACH ONE ASKS FOR ITS OWN THING WHEN IT IS OPENED. The
 * locked screen has About · Signature Menu · Happy Hour · Daily Deal ·
 * Last-Minute Special · Game Day · Feeds · Reviews. Fetching all eight on
 * arrival would be eight requests to show one, on a phone, on Hong Kong
 * mobile data.
 *
 * ⚠️ AND EVERY ROUTE BELOW ALREADY EXISTED. Not one was written for this
 * screen — the venue page is the one part of Eleat the backend already serves
 * end to end, which is why it is the right thing to build first.
 */
export const venue = {
  get:     (id) => call(`/restaurants/${id}`),
  status:  (id) => call(`/restaurants/${id}/status`),
  menu:    (id) => call(`/menu/restaurant/${id}`),
  /* ⚠️ THE FOUR DEAL TYPES ARE THE SCHEMA'S OWN WORDS — happy_hour, daily,
   * last_minute, game_day (001_schema.sql, `deal_type`). The tab LABELS are
   * different and deliberately so: the venue page says "Daily Deal" singular
   * because you are looking at one venue's one deal, where Home says "Daily
   * Deals" because it is showing many. Never let the label and the key drift
   * into each other. */
  /* ⚠️ NO TYPE MEANS EVERY TYPE, AND THAT IS THE IMPORTANT CALL.
   * 22 September 2026. This always appended `?type=…`, so with no type it sent
   * the literal string "undefined" and the route's enum rejected it. The venue
   * page therefore had to ask four times, once per deal type, and still had no
   * way to know which types a venue actually has — so it drew all four tabs
   * regardless. One call answers both questions at once. */
  deals:   (id, type) => call('/deals/restaurant/' + id
    + (type ? '?type=' + encodeURIComponent(type) : '')),
  /* ⚠️⚠️ THE GAMES, AND THIS IS NOT THE SAME THING AS A GAME DAY DEAL.
   * `deals(id,'game_day')` returns the offers. This returns **what is
   * playing** — the game, the league, the kick-off, the duration — plus the
   * venue's one Game Day menu and its two window offsets, from the tables
   * `019_game_day_one_menu.sql` created in August and nothing had ever read.
   * One call, because the pane filters by sport, week, day, game, phase and
   * category and six requests is six chances for two of them to disagree. */
  gameDay: (id) => call('/game-day/venue/' + id),
  /* ⚠️ `/feeds`, PLURAL — app.js:110 is `app.use('/feeds', feedRoutes)`.
   * I wrote `/feed` and it returned 404 on every venue, every time. The module
   * is called `feeds`, the service is `feedRoutes`, and the singular read
   * perfectly well to me while matching nothing at all. Verified against the
   * live API on 18 September: `/feed/restaurant/<id>` → 404, `/feeds/…` → 200.
   * Checked by calling it, not by reading it. */
  feeds:   (id) => call(`/feeds/restaurant/${id}`),
  /* ⚠️ LIKING A POST IS `feed_saves`, NOT `saved_restaurants` — two tables and
   * two different actions, which is why the locked viewer draws two controls
   * with two words under them. The routes have existed since the day they were
   * written and no screen had ever called one. */
  likeFeed: (feedId, on) => call(`/feeds/${feedId}/save`,
    { method: on ? 'POST' : 'DELETE', auth: true }),
  reviews: (id) => call(`/reviews/restaurant/${id}`),
  /* 226 · Google's stars and up to five Google reviews, fetched live, ONLY while
     the venue has fewer than five verified Eleat reviews (Al-Noor, 3 Nov 2026).
     { show:false } otherwise — and at five the server never asks Google. */
  googleReviews: (id, lang) => call(`/restaurants/${id}/google-reviews?lang=${encodeURIComponent(lang || 'en')}`),

  /* ⚠️ WHETHER THIS ELEATER MAY WRITE A REVIEW IS THE SERVER'S ANSWER, NOT A
   * GUESS ON THE SCREEN. `GET /visits/can-review/:restaurantId`
   * (visits.routes.js:47, eleater-only) answers `{can_review, reason, message}`
   * — and the message it returns for `no_visit` is the locked screen's own
   * hint, word for word: "Reviews come from a confirmed visit."
   *
   * ⚠️ IT IS READ-ONLY. `ensureVisit` was removed from that path precisely
   * because a GET was writing rows; asking the question does not create a
   * visit, so the button may ask it freely. */
  canReview: (id) => call(`/visits/can-review/${id}`, { auth: true }),
  /* "Did you make it to ___?" — 227, Al-Noor 3 Nov 2026 */
  dinedAnswer: (reservationId, answer) =>
    call(`/reservations/${encodeURIComponent(reservationId)}/dined`, { method: 'POST', auth: true, body: { answer } }),

  /* ⚠️ THE WRITE ROUTE IS REAL AND GATED. `POST /reviews/restaurant/:id`
   * requires a signed-in ELEATER and, underneath, a confirmed dining visit
   * that has not already been reviewed. Nothing here relaxes that — the
   * screen asks canReview() first so an Eleater is told why, rather than
   * filling in a form and being refused at the end. */
  writeReview: (id, body) =>
    call(`/reviews/restaurant/${id}`, { method: 'POST', auth: true, body }),

  /* ⚠️ THE HEART NEEDS A SIGN-IN AND THE SCREEN MUST KNOW THAT BEFORE IT IS
   * TAPPED. A signed-out Eleater tapping it gets a 401, and an error where a
   * filled heart should be is the dead button this project keeps clearing. */
  isSaved: (id) => call(`/restaurants/${id}/saved`, { auth: true }),
  save:    (id) => call(`/restaurants/${id}/save`, { method: 'POST', auth: true }),
  /* ⚠️⚠️ THE HEART WAS ONE-WAY AND NOTHING SAID SO. `POST /save` inserts with
   * `on conflict do nothing` and ALWAYS answers `{saved:true}` — so tapping a
   * filled heart re-saved the same row and the heart stayed filled for ever.
   * `DELETE /restaurants/:id/save` has existed since the route was written
   * and nothing in the app called it. A control that only works in one
   * direction is worse than one that does nothing: it looks like it obeyed. */
  unsave:  (id) => call(`/restaurants/${id}/save`, { method: 'DELETE', auth: true }),
};

export function signIn({ identifier, password }) {
  const id = String(identifier || '').trim();
  /* side — 144: an Eleater account, never the restaurant login on the same email */
  const body = id.includes('@') ? { email: id, password, side: 'eleater' } : { phone: id, password, side: 'eleater' };
  return call('/auth/password/login', { method: 'POST', body });
}

/* ══════════════════════════════════════════════════════════════════════════
 * ASK HER AND READ THE ANSWER AS IT IS WRITTEN
 *
 * Al, 27 September 2026: *"first words appear in about one second, feels
 * instant. Yes, do that."*
 *
 * ⚠️ IT RETURNS `null` RATHER THAN THROWING WHEN THE STREAM CANNOT RUN, and
 * that is the whole design. Zaryna is the app's headline feature and this
 * session could not build or test the app — so a proxy that buffers, a
 * browser without `ReadableStream`, or a server caught mid-deploy must cost
 * nobody their question. The caller does `await askStreaming(...) || await
 * call('/zaryna/chat', …)`: **a transport fallback, not two versions of a
 * product decision.**
 *
 * ⚠️ `fetch` HERE AND NOT `call()`, BECAUSE `call()` READS THE WHOLE BODY.
 * It ends in `res.json()`, which waits for the last byte — the exact thing
 * this exists to avoid. The auth header and the base URL are the same.
 *
 * ⚠️ AND NO TIMEOUT ABORT, UNLIKE `call()`. Its 45-second limit exists
 * because a promise that never settles looks identical to a slow one; a
 * stream that has already delivered words is visibly alive, and cutting it at
 * a fixed moment would truncate an answer mid-sentence — which is a fault
 * this project has already shipped once.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️ A RANDOM ID FOR THIS PHONE, NEVER A PERSON — Zaryna answers visitors too
   (Al-Noor, 11 Oct 2026) and the daily limit counts a visitor's questions by
   it (189). Sent only on her own calls, never on browsing. */
export function deviceId() {
  try {
    let d = localStorage.getItem('eleat.device');
    if (!d) {
      d = (crypto && crypto.randomUUID) ? crypto.randomUUID()
        : 'd' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('eleat.device', d);
    }
    return d;
  } catch { return ''; }
}

export async function askStreaming(body, onDelta, onFound, onCards) {
  if (typeof fetch !== 'function' || typeof TextDecoder !== 'function') return null;

  let res;
  /* ⚠️ NEVER WAIT FOR EVER — 11 Oct 2026: a stream that never ended left the
     screen on "Searching…" with nothing to tap. 90 seconds, then it stops. */
  const stopwatch = new AbortController();
  setTimeout(() => stopwatch.abort(), 90000);
  try {
    const t = getToken();
    res = await fetch(`${BASE}/zaryna/chat/stream`, {
      signal: stopwatch.signal,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Eleat-Device': deviceId(),
        ...(previewToken() ? { 'X-Eleat-Preview': previewToken() } : {}),
        /* signed in: answered as themselves; a visitor: answered all the same */
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch { return null; }

  /* ⚠️ NOT OK, OR NO READABLE BODY → hand it back to the caller untouched.
     Nothing has been shown on screen yet, so the fallback is invisible. */
  if (!res.ok || !res.body || !res.body.getReader) return null;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let done = null;
  let sawAnything = false;

  try {
    for (;;) {
      /* eslint-disable no-await-in-loop */
      const { value, done: finished } = await reader.read();
      if (finished) break;
      buf += decoder.decode(value, { stream: true });

      /* ⚠️ SPLIT ON A BLANK LINE, WHICH IS WHAT ENDS AN SSE MESSAGE — not on
         every newline. Her answers contain line breaks between venues, and
         the server JSON-encodes each payload for exactly that reason. */
      const parts = buf.split('\n\n');
      buf = parts.pop() || '';

      for (const block of parts) {
        let event = 'message';
        let data = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data += line.slice(5).trim();
        }
        if (!data) continue;
        let payload;
        try { payload = JSON.parse(data); } catch { continue; }

        if (event === 'text') { sawAnything = true; if (onDelta) onDelta(payload); }
        else if (event === 'found') { if (onFound) onFound(payload); }
        /* the cards before her line (Al-Noor, 31 Oct 2026) */
        else if (event === 'cards') { if (onCards) onCards(payload); }
        else if (event === 'done') { done = payload; }
        else if (event === 'error') { done = payload; }
      }
    }
  } catch {
    /* ⚠️ IF WORDS WERE ALREADY SHOWN, DO NOT FALL BACK. Running the whole
       question again would answer it twice on screen. The caller sees `done`
       as null and simply stops — half an answer already read is better than
       two of them. */
    if (sawAnything) return { reply: null, streamed: true, broke: true };
    return null;
  }

  /* Nothing arrived at all and no `done` — treat it as unavailable so the
     ordinary route answers. */
  if (!done && !sawAnything) return null;
  return done || { reply: null, streamed: true, broke: true };
}
